import React, { useEffect } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../../redux/store'
import { getAllMissions } from '../../../api/missionsApi'
import { getIncidentsByMissionIdApi } from '../../../api/incidentApi'
import { useAsyncOptions } from '../../../hooks/useAsyncOptions'
import SelectMission from '../../../components/common/SelectMission'
import SelectAudit from '../../audit/SelectAudit'
import SelectIncident from '../../Incident/SelectIncident'
import SelectRisque from '../../risque/SelectRisque'
import SelectProjet from '../../projets/SelectProjet'
import SecurityControlsSelect from '../../securityCheckFamily/SecurityCheckFamilyMain'
import SelectSubDomain from '../../../components/common/selectSubDomain'
import SelectSecurity from '../../../components/common/SelectSecurity'
import { FormField, FormGrid, FormSection } from '../../../components/common/form'
import { FieldError, ImportContext, RowErrors } from './importRowModel'
import QuickCreate, { QuickCreated, useQuickCreate } from '../../../components/quickCreate/QuickCreate'
import { QuickEntity } from '../../../components/quickCreate/quickCreateErrors'

type ContextFieldsProps = {
  value: ImportContext
  onChange: (next: ImportContext) => void
  onLabels: (labels: Record<string, string>) => void
  errors?: RowErrors
  /** Mark mission (and security fields) as required in the labels. */
  strict?: boolean
  /** Rendered under the mission/project fields (e.g. budget summaries). */
  missionSlot?: React.ReactNode
  projectSlot?: React.ReactNode
  /** "+ New …" inside every origin select, created in a stacked popup. */
  quickCreate?: boolean
  /** Bump to refetch every option list. */
  reloadKey?: number
  cols?: 1 | 2 | 3
  /** Mission was set by the screen that opened the import. */
  lockMission?: boolean
  /** Project was set by the screen that opened the import. */
  lockProject?: boolean
}

const LABEL_FIELD: Partial<Record<QuickEntity, string>> = {
  mission: 'MissionName',
  project: 'ProjetName',
  audit: 'AuditName',
  incident: 'IncidentName',
  risk: 'RisqueName',
}

export function FieldErrorText({ error }: { error?: FieldError }) {
  const { t } = useTranslation()
  if (!error) return null
  return (
    <span className="action-import-field-error" role="alert">
      {t(error.key, error.values)}
    </span>
  )
}

const first = (ids: string[]) => ids[0] || ''

const single = (value: string | string[]) => {
  const id = Array.isArray(value) ? value[0] : value
  return id ? [id] : []
}

function incidentsOf(payload: unknown): { _id?: string; IncidentName?: string }[] {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    if (Array.isArray(record.incidents)) return record.incidents as any[]
    return Object.values(record).filter(
      (item) => item && typeof item === 'object' && !Array.isArray(item)
    ) as any[]
  }
  return []
}

/**
 * Mission / project / sources / security fields of an action, wired like
 * /addaction (same selects, same cascade). Option lists live in shared Redux
 * slices, so only one instance should be mounted at a time.
 */
export default function ContextFields({
  value,
  onChange,
  onLabels,
  errors = {},
  strict = false,
  missionSlot,
  projectSlot,
  quickCreate = true,
  reloadKey = 0,
  cols = 3,
  lockMission = false,
  lockProject = false,
}: ContextFieldsProps) {
  const { t } = useTranslation()
  const qc = useQuickCreate()
  const keyOf = (kind: QuickEntity) => reloadKey + qc.keyOf(kind)
  const createFor = (kind: QuickEntity) =>
    quickCreate ? qc.open(kind, kind === 'mission' ? undefined : value.MissionId) : undefined

  const projets = useSelector((s: RootState) => s.projet.ProjetResponse?.projets)
  const audits = useSelector((s: RootState) => s.audit.audits)
  const risques = useSelector((s: RootState) => s.risque.risques)
  const families = useSelector(
    (s: RootState) => s.securitycheckFamily.SecurityCheckFamilyResponse?.securityCheckFamilies
  )
  const controls = useSelector((s: RootState) => s.securitycheckFamily.SecurityControls)
  const subDomains = useSelector((s: RootState) => s.subDomain.subDomainsbySecurityControls)

  const { data: missions } = useAsyncOptions(async () => {
    const response = await getAllMissions({ search: '', page: 1, pageSize: 200 })
    return response.missions ?? []
  }, [keyOf('mission')])

  const { data: incidents } = useAsyncOptions(
    async () => incidentsOf((await getIncidentsByMissionIdApi(value.MissionId)).data),
    [value.MissionId, keyOf('incident')],
    Boolean(value.MissionId)
  )

  useEffect(() => {
    const labels: Record<string, string> = {}
    const add = (id?: string, label?: string) => {
      if (id && label) labels[id] = label
    }
    missions?.forEach((m) => add(m._id, m.MissionName))
    projets?.forEach((p) => add(p._id, p.ProjetName))
    if (Array.isArray(audits)) audits.forEach((a) => add(a._id, a.AuditName))
    incidents?.forEach((i) => add(i._id, i.IncidentName))
    if (Array.isArray(risques)) risques.forEach((r) => add(r._id, r.RisqueName))
    families?.forEach((f) => add(f._id, f.LabelSCF))
    controls?.forEach((c) => add(c._id, c.Label))
    if (Array.isArray(subDomains)) subDomains.forEach((d) => add(d._id, d.LabelSD))
    if (Object.keys(labels).length) onLabels(labels)
    // onLabels is a dispatch wrapper; re-run only when option lists change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missions, projets, audits, incidents, risques, families, controls, subDomains])

  const patch = (changes: Partial<ImportContext>) => onChange({ ...value, ...changes })

  const handleMission = (MissionId: string) => {
    if (lockMission || MissionId === value.MissionId) return
    patch({
      MissionId,
      ProjetId: '',
      SourceAudit: [],
      SourceIncident: [],
      SourceRisque: [],
    })
  }

  const handleCreated = (kind: QuickEntity, created: QuickCreated) => {
    qc.close()
    qc.bump(kind)
    const id = created._id as string
    const labelField = LABEL_FIELD[kind]
    if (labelField && created[labelField]) onLabels({ [id]: String(created[labelField]) })
    if (kind === 'mission') handleMission(id)
    else if (kind === 'project') patch({ ProjetId: id })
    else if (kind === 'audit') patch({ SourceAudit: [...value.SourceAudit, id] })
    else if (kind === 'incident') patch({ SourceIncident: [...value.SourceIncident, id] })
    else if (kind === 'risk') patch({ SourceRisque: [...value.SourceRisque, id] })
  }

  const handleFamily = (e: SelectChangeEvent<string | string[]>) => {
    const SecurityIds = single(e.target.value)
    if (first(SecurityIds) === first(value.SecurityIds)) return
    patch({ SecurityIds, SecurityControlIds: [], SubDomainIds: [] })
  }

  const handleControl = (e: SelectChangeEvent<string | string[]>) => {
    const SecurityControlIds = single(e.target.value)
    if (first(SecurityControlIds) === first(value.SecurityControlIds)) return
    patch({ SecurityControlIds, SubDomainIds: [] })
  }

  const noMission = (
    <div className="action-import-placeholder">{t('actionImport.context.pickMissionFirst')}</div>
  )

  return (
    <>
      <FormSection
        title={t('actionCreate.sections.origin.title')}
        description={t('actionImport.context.originDesc')}
      >
        <FormGrid cols={cols}>
          <FormField label={t('actionCreate.sections.origin.mission')} required>
            <SelectMission
              value={value.MissionId}
              onChange={handleMission}
              onCreateNew={lockMission ? undefined : createFor('mission')}
              reloadKey={keyOf('mission')}
              disabled={lockMission}
            />
            <FieldErrorText error={errors.MissionId} />
            {value.MissionId ? missionSlot : null}
          </FormField>
          <FormField label={t('actionCreate.sections.origin.project')}>
            {value.MissionId ? (
              <SelectProjet
                value={value.ProjetId}
                onChange={(e) => {
                  if (!lockProject) patch({ ProjetId: e.target.value })
                }}
                MissionId={value.MissionId}
                onCreateNew={lockProject ? undefined : createFor('project')}
                reloadKey={keyOf('project')}
                disabled={lockProject}
              />
            ) : (
              noMission
            )}
            <FieldErrorText error={errors.ProjetId} />
            {value.ProjetId ? projectSlot : null}
          </FormField>
          <FormField label={t('actionCreate.sections.origin.audit')}>
            {value.MissionId ? (
              <SelectAudit
                value={value.SourceAudit}
                onChange={(e) => patch({ SourceAudit: e.target.value as string[] })}
                MissionId={value.MissionId}
                onCreateNew={createFor('audit')}
                reloadKey={keyOf('audit')}
              />
            ) : (
              noMission
            )}
            <FieldErrorText error={errors.Sources} />
          </FormField>
          <FormField label={t('actionCreate.sections.origin.incident')}>
            {value.MissionId ? (
              <SelectIncident
                value={value.SourceIncident}
                onChange={(e) => patch({ SourceIncident: e.target.value as string[] })}
                MissionId={value.MissionId}
                onCreateNew={createFor('incident')}
                reloadKey={keyOf('incident')}
              />
            ) : (
              noMission
            )}
          </FormField>
          <FormField label={t('actionCreate.sections.origin.risk')}>
            {value.MissionId ? (
              <SelectRisque
                value={value.SourceRisque}
                onChange={(e) => patch({ SourceRisque: e.target.value as string[] })}
                MissionId={value.MissionId}
                onCreateNew={createFor('risk')}
                reloadKey={keyOf('risk')}
              />
            ) : (
              noMission
            )}
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection
        title={t('actionCreate.sections.security.title')}
        description={
          strict
            ? t('actionCreate.sections.security.desc')
            : t('actionImport.context.securityOptionalDesc')
        }
      >
        <FormGrid cols={cols}>
          <FormField
            label={t('Security Check Family', { defaultValue: 'Security Check Family' })}
            required={strict}
          >
            <SelectSecurity value={first(value.SecurityIds)} onChange={handleFamily} multiple={false} />
            <FieldErrorText error={errors.SecurityIds} />
          </FormField>
          <FormField
            label={t('Security Control', { defaultValue: 'Security Control' })}
            required={strict}
          >
            {value.SecurityIds.length ? (
              <SecurityControlsSelect
                value={first(value.SecurityControlIds)}
                onChange={handleControl}
                securityId={first(value.SecurityIds)}
                multiple={false}
              />
            ) : (
              <div className="action-import-placeholder">
                {t('actionImport.context.pickFamilyFirst')}
              </div>
            )}
            <FieldErrorText error={errors.SecurityControlIds} />
          </FormField>
          <FormField label={t('SubDomain', { defaultValue: 'SubDomain' })} required={strict}>
            {value.SecurityControlIds.length ? (
              <SelectSubDomain
                securityControlIds={first(value.SecurityControlIds)}
                selectedSubDomainIds={first(value.SubDomainIds)}
                onChange={(ids) => patch({ SubDomainIds: single(ids) })}
                multiple={false}
              />
            ) : (
              <div className="action-import-placeholder">
                {t('actionImport.context.pickControlFirst')}
              </div>
            )}
            <FieldErrorText error={errors.SubDomainIds} />
          </FormField>
        </FormGrid>
      </FormSection>
      <QuickCreate request={qc.request} onClose={qc.close} onCreated={handleCreated} />
    </>
  )
}
